import React from 'react';
import { Link } from 'react-router-dom';
import { 
  GraduationCap, 
  Users, 
  BookOpen, 
  Award,
  ChevronLeft,
  Play
} from 'lucide-react';

const About = () => {
  const features = [
    {
      icon: BookOpen,
      title: 'محتوى تعليمي متميز',
      description: 'دروس شاملة ومتكاملة لجميع مواد الثانوية العامة مع شروحات واضحة ومبسطة'
    },
    {
      icon: Users,
      title: 'مدرسون نخبة',
      description: 'أفضل المدرسين المصريين المتخصصين في كل مادة من مواد الثانوية العامة'
    },
    {
      icon: Award,
      title: 'شهادات معتمدة',
      description: 'شهادات إتمام معتمدة بعد كل كورس تساعدك في رحلتك التعليمية'
    },
    {
      icon: Play,
      title: 'فيديوهات عالية الجودة',
      description: 'محتوى فيديو احترافي مسجل بأعلى جودة لتجربة تعليمية ممتازة'
    }
  ];

  const stats = [
    { number: '10,000+', label: 'طالب مسجل' },
    { number: '500+', label: 'درس فيديو' },
    { number: '50+', label: 'مدرس متخصص' },
    { number: '95%', label: 'نسبة رضا الطلاب' }
  ];

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="w-20 h-20 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
              <GraduationCap size={40} />
            </div>
            <h1 className="text-4xl font-black mb-4">عن منصة "منصتي"</h1>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              منصتك التعليمية الأولى للثانوية العامة في مصر - نحقق لك التفوق والنجاح
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center">
              <h3 className="text-3xl font-black text-primary-600 mb-2">{stat.number}</h3>
              <p className="text-sm font-bold text-gray-600">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* About Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-3xl font-black text-gray-900 mb-6">من نحن؟</h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                "منصتي" هي المنصة التعليمية الرائدة في مصر المتخصصة في طلاب الثانوية العامة. بدأنا رحلتنا بهدف واحد: توفير تعليم متميز ومتاح للجميع.
              </p>
              <p>
                نؤمن بأن كل طالب لديه القدرة على التفوق إذا توفرت له الأدوات الصحيحة والإرشاد السليم. لهذا السبب، جمعنا أفضل المدرسين وأحدث الأساليب التعليمية في منصة واحدة.
              </p>
              <p>
                منصتنا ليست مجرد مكان للدروس، بل هي بيئة تعليمية متكاملة تدعم رحلتك من بداية المرحلة الثانوية وحتى التخرج بتفوق.
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-black text-gray-900 mb-6">رؤيتنا</h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                نسعى ليكون كل طالب مصري قادر على الوصول إلى تعليم عالي الجودة بغض النظر عن مكانه أو ظروفه.
              </p>
              <p>
                رؤيتنا هي إعادة تعريف التعليم في مصر باستخدام التكنولوجيا الحديثة والأساليب المبتكرة التي تجعل التعلم ممتعاً وفعالاً.
              </p>
              <p>
                نهدف إلى تخريج جيل من الطلاب الواثقين بأنفسهم، القادرين على التفكير النقدي، والمجهزين لمواجهة تحديات المستقبل.
              </p>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-black text-gray-900 mb-8 text-center">لماذا تختار منصتي؟</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-primary-50 text-primary-600 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon size={24} />
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Values Section */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 mb-16">
          <h2 className="text-3xl font-black text-gray-900 mb-8 text-center">قيمنا</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <h3 className="text-xl font-black text-primary-600 mb-3">الجودة</h3>
              <p className="text-gray-600">نلتزم بتقديم محتوى تعليمي عالي الجودة يلبي أعلى المعايير</p>
            </div>
            <div className="text-center">
              <h3 className="text-xl font-black text-primary-600 mb-3">الابتكار</h3>
              <p className="text-gray-600">نستخدم أحدث التقنيات والأساليب التعليمية المبتكرة</p>
            </div>
            <div className="text-center">
              <h3 className="text-xl font-black text-primary-600 mb-3">التميز</h3>
              <p className="text-gray-600">نسعى لتحقيق التفوق في كل جانب من جوانب تجربة التعلم</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-3xl p-8 text-center">
          <h2 className="text-3xl font-black text-white mb-4">انضم إلى آلاف الطلاب الناجحين</h2>
          <p className="text-primary-100 mb-8 max-w-2xl mx-auto">
            ابدأ رحلتك نحو التفوق في الثانوية العامة مع منصة "منصتي" اليوم
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-white text-primary-600 px-8 py-3 rounded-2xl font-black hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl"
          >
            ابدأ الآن
            <ChevronLeft size={20} className="rotate-180" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default About;
